/**
 * Door / window / opening clearance for manually-drawn cabinet runs.
 *
 * A base cabinet can't sit in a doorway (or a floor-level pass-through, or
 * under a low window). For each run segment we treat such openings on that
 * segment's wall as obstacle reservations — the same partition mechanism
 * appliances use — so the fill skips their span (plus a small buffer).
 *
 * Which openings block a BASE cabinet: any whose sill (bottom edge) sits below
 * the countertop surface. Doors and floor-level openings have a ~0 sill so they
 * always block; a window only blocks if it's low-silled (high windows let a
 * base cabinet tuck underneath — uppers are a separate, v2 concern). Pure.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  OPENING_BUFFER,
  OPENING_FRONT_CLEARANCE,
  INCHES_TO_METERS,
} = require('../kitchen-assembly/constants');
const { worldToWallParam } = require('../kitchen-assembly/wallMath');
const { legHolds } = require('../kitchen-assembly/legRule');
const {
  DEFAULT_CORNER_L_SET,
  legTakes,
} = require('../kitchen-assembly/cornerBlind');
const {
  baysForRun,
  isApplianceKind,
  HOSTED_BAY_KINDS,
  RUN_TYPE_BY_BAY_KIND,
} = require('./bays');
const { SPAN_EPS } = require('./spanPacking');
const { cornerBoxFor } = require('./runDepth');
const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
const { isAutoRun } = require('./runSource');
const {
  getOpeningClearanceZones,
  objectConflictsWithZones,
  openingKeepOuts,
} = require('../kitchen-assembly/openingClearance');

// Height of the finished countertop above the floor — the sill threshold below
// which an opening blocks a base cabinet. The KITCHEN's, and the fallback for
// a caller that does not know the run: the real answer is the run's own
// (`ctx.counterTop`, from `profiles.counterTopOffsetFor`), because a vanity's
// counter is 33.5" and a sill between there and 36" clears it.
const COUNTERTOP_HEIGHT =
  KITCHEN_CABINET_DIMENSIONS.height + KITCHEN_COUNTERTOP_THICKNESS;

/** The counter line these rules measure a sill against, for THIS run. */
function counterTopYOf(ctx) {
  return (ctx?.floorY || 0) + (ctx?.counterTop ?? COUNTERTOP_HEIGHT);
}

const SILL_EPS = 1e-6;
// Clearance kept in front of a door / floor-opening on an ADJACENT wall, so a
// base cabinet near the corner doesn't sit in the neighbouring doorway. 30"
// (passage), NOT the 54" swing arc — base cabs sit below the door leaf.
const ADJ_OPENING_CLEARANCE = OPENING_FRONT_CLEARANCE; // 30"

/**
 * A floor-reaching opening blocks a base cabinet (sill below the countertop).
 *
 * `_baseCabinetExempt` overrides the sill test. It is set by the SCAN path for
 * a window RoomPlan measured cabinets running across: measured cabinetry beats
 * a generative keep-out, and a reservation there would delete a run the scan
 * actually saw (see post-processing/window-sill-lift.js — the lift is the
 * primary fix; this flag is for the windows that cannot be lifted). Nothing
 * else about the opening changes: it still blocks UPPERS, which read
 * `allOpeningReservationsForSegment` and never consult the sill.
 */
function blocksBaseCabinet(o, counterTopY) {
  if (!o.center || !o.dimensions) return false;
  if (o._baseCabinetExempt) return false;
  const sillY = o.center.y - (o.dimensions.y || 0) / 2;
  return sillY < counterTopY - SILL_EPS;
}

/**
 * Distance from a corner vertex (at `vertexParam`, with `legDir` pointing into
 * the leg) to the nearest blocking opening's near edge on `wall`, or Infinity
 * if none. 0 when an opening straddles the vertex; one wholly behind it (the
 * wall running on past a mid-span junction) is not on the leg.
 */
function nearestBlockingOpening(
  wall,
  vertexParam,
  legDir,
  openings,
  counterTopY
) {
  return nearestBlockingOpeningAt(
    wall,
    vertexParam,
    legDir,
    openings,
    counterTopY
  ).d;
}

/** `nearestBlockingOpening`, with WHICH opening it is: `{ d, id }`. */
function nearestBlockingOpeningAt(
  wall,
  vertexParam,
  legDir,
  openings,
  counterTopY
) {
  let best = { d: Infinity, id: null };
  for (const o of openings || []) {
    if ((o.parentWallIds || [])[0] !== wall.id) continue;
    if (!blocksBaseCabinet(o, counterTopY)) continue;
    const p = worldToWallParam(wall, o.center);
    const nearEdge = p - legDir * (o.dimensions.x / 2); // edge toward the vertex
    const farEdge = p + legDir * (o.dimensions.x / 2);
    // An opening wholly BEHIND the vertex is not on this leg: where a
    // peninsula lands mid-span the wall runs on past the corner, and a doorway
    // beyond the junction read as standing in the corner box (clamped to 0),
    // emptying a corner the generator had built.
    if ((farEdge - vertexParam) * legDir <= 0) continue;
    const dist = (nearEdge - vertexParam) * legDir; // into the leg from the vertex
    const d = Math.max(0, dist);
    if (d < best.d) best = { d, id: o.id ?? null };
  }
  return best;
}

/**
 * Obstacle reservations (param ranges) for the floor-reaching openings on a
 * segment's wall, each widened by OPENING_BUFFER on both sides. Sorted by start.
 * `ctx.openings` is a flat list of { id, parentWallIds, center, dimensions }.
 */
function openingReservationsForSegment(run, segIndex, ctx) {
  const seg = (run.segments || [])[segIndex];
  const wall = seg && seg.wall;
  if (!wall) return [];
  const counterTopY = counterTopYOf(ctx);

  const res = [];
  for (const o of ctx.openings || []) {
    if ((o.parentWallIds || [])[0] !== wall.id) continue;
    if (!blocksBaseCabinet(o, counterTopY)) continue;
    const param = worldToWallParam(wall, o.center);
    const half = (o.dimensions.x || 0) / 2 + OPENING_BUFFER;
    res.push({ lo: param - half, hi: param + half, openingId: o.id });
  }
  res.sort((a, b) => a.lo - b.lo);
  return res;
}

/**
 * Obstacle reservations for EVERY opening on a segment's wall — windows AND
 * doors, regardless of sill height — each widened by OPENING_BUFFER on both
 * sides. Sorted by start. This is the UPPER-cabinet variant of
 * openingReservationsForSegment: an upper cabinet must skip a gap over any
 * opening (a high window the base tucks under still wants an open wall above
 * it, and a door obviously can't have an upper over it). Pure.
 */
function allOpeningReservationsForSegment(run, segIndex, ctx) {
  const seg = (run.segments || [])[segIndex];
  const wall = seg && seg.wall;
  if (!wall) return [];

  const res = [];
  for (const o of ctx.openings || []) {
    if ((o.parentWallIds || [])[0] !== wall.id) continue;
    if (!o.center || !o.dimensions) continue;
    const param = worldToWallParam(wall, o.center);
    const half = (o.dimensions.x || 0) / 2 + OPENING_BUFFER;
    res.push({
      lo: param - half,
      hi: param + half,
      openingId: o.id,
      // World Y of the opening's head: the upper tier reads it to tell an
      // opening it must give way to from one it can run across.
      topY: o.center.y + (o.dimensions.y || 0) / 2,
    });
  }
  res.sort((a, b) => a.lo - b.lo);
  return res;
}

/**
 * Span on `wall` already occupied by cabinetry the SCAN measured, as [lo, hi]
 * params. Only bodies parented to this wall count — a body round the corner
 * projects onto this wall's axis too, and would wrongly excuse a reservation.
 */
function measuredSpansOnWall(wall, bodies) {
  const spans = [];
  for (const o of bodies || []) {
    if ((o.parentWallIds || [])[0] !== wall.id) continue;
    if (!o.center || !o.dimensions) continue;
    const mid = worldToWallParam(wall, o.center);
    if (mid == null) continue;
    const half = (o.dimensions.x || 0) / 2;
    if (half <= 0) continue;
    spans.push([mid - half, mid + half]);
  }
  return spans.sort((a, b) => a[0] - b[0]);
}

/** `[lo, hi]` minus every span in `cuts` — 0, 1 or 2 surviving pieces. */
function subtractSpans(lo, hi, cuts) {
  let pieces = [[lo, hi]];
  for (const [cLo, cHi] of cuts) {
    const next = [];
    for (const [pLo, pHi] of pieces) {
      if (cHi <= pLo || cLo >= pHi) {
        next.push([pLo, pHi]);
        continue;
      }
      if (cLo > pLo) next.push([pLo, cLo]);
      if (cHi < pHi) next.push([cHi, pHi]);
    }
    pieces = next;
  }
  return pieces.filter(([a, b]) => b - a > SPAN_EPS);
}

/**
 * Obstacle reservations for floor-reaching openings on OTHER walls (adjacent /
 * perpendicular to this segment's wall) whose clearance zone reaches across the
 * corner into the segment's cabinet-depth strip — so a base cabinet near the
 * corner doesn't land in a doorway on the neighbouring wall. The same
 * `blocksBaseCabinet` sill test applies (doors / low windows block; high
 * windows don't). `ctx.walls` is the full list of engine walls (carrying
 * `alignment`) so any opening's parent wall can be resolved + projected.
 *
 * `ctx.measuredBodies` (scan-derived runs only) carves out span the scan
 * actually MEASURED cabinetry in. This reservation is a DESIGN rule — don't
 * plan a cabinet into the neighbouring doorway's approach — which is right when
 * we are choosing where cabinets go and wrong when the cabinet already exists:
 * there it deletes a measured thing to satisfy a preference. On 08E54F9E the
 * 46.9" opening's 30" front zone projects onto the last 31.0" of the kitchen's
 * south wall, where RoomPlan measured a 76.5" floor cabinet running to within
 * 6.6" of the wall's end — so 24.4" of real cabinetry (and its countertop) was
 * dropped, leaving a scanned upper hanging over bare wall. 886A9359 loses 23.5"
 * the same way. The reservation still applies to every span the scan left
 * empty, so a fill can never EXTEND into the approach; and the generated and
 * manually-drawn paths pass no `measuredBodies`, so they are unchanged.
 *
 * Note this deliberately keeps the reservation where the scan is silent rather
 * than dropping it for scan runs wholesale — the rule is still right about
 * space nobody built in.
 *
 * A WIDE door-less opening (over 48", `passageWidth`) is judged on the way
 * through it instead (#2822): with the room's other runs standing as they are
 * (`ctx.neighbours`) and this run reaching into the zone, is there still a 48"
 * passage? Then there is no reservation. It is the generator's own question
 * (`openingKeepOuts`), so a run the generator planned to the corner of an
 * archway is built there — at Golden West, Temple City the flat 30" band left
 * 8.5" no cabinet fits, beside a 126" archway the run took 24" of. A run that
 * would close the passage keeps the reservation, and whichever run is filled
 * first makes no difference: each is asked with the others as they stand.
 *
 * "This run" is EVERY wall-backed segment of it, each over the stretch of its
 * wall it covers, and this segment takes its share of that one answer. The
 * neighbours are other runs only, so asked with its own wall alone a U whose
 * two arms flank an archway let each arm in as if the other were not there:
 * on Oxford / Barry Ave a one-run U left 44" of an 84.5" archway, where the
 * same walls as two runs kept 69".
 */
function adjacentOpeningReservationsForSegment(run, segIndex, ctx) {
  // A VANITY a layout PLANNED (`build_vanity` derives it, `source: 'auto'`)
  // is built at the width the layout planned, and the bathroom generator
  // already measured that width against the openings around it — this rule
  // is the run choosing where cabinets go, and here the layout already
  // chose. Kept, it trimmed the reported 88.7" Venus double 14.35" short of
  // its corner for a window round it (sill 25.1", starting at the corner):
  // the counter, the 12" base and the filler of that end were never built
  // under a mirror hung the full width. A drawn vanity run, a scanned one and
  // every kitchen keep the rule.
  if (profileOf(run).id === VANITY_PROFILE_ID && isAutoRun(run)) return [];
  const seg = (run.segments || [])[segIndex];
  const runWall = seg && seg.wall;
  if (!runWall || !Array.isArray(ctx.walls)) return [];
  const counterTopY = counterTopYOf(ctx);
  const measured = measuredSpansOnWall(runWall, ctx.measuredBodies);
  // Floor-reaching openings on OTHER walls (same-wall handled above): a sill
  // below this run's countertop.
  const adjacent = (ctx.openings || []).filter((o) => {
    const oWallId = (o.parentWallIds || [])[0];
    return (
      oWallId && oWallId !== runWall.id && blocksBaseCabinet(o, counterTopY)
    );
  });
  if (!adjacent.length) return [];
  const zones = getOpeningClearanceZones(
    { walls: ctx.walls, ...openingsByKind(adjacent) },
    { frontClearance: ADJ_OPENING_CLEARANCE }
  );
  // Each zone projected onto the run's walls, kept where it is within a
  // cabinet depth of the wall (actually in front of a base cabinet's
  // footprint). THIS run's depth, not the standard 24" — the same rule
  // `cornerBoxFor` states for the corner box, and for the same reason: a 21"
  // vanity has a shallower footprint to keep clear of the doorway, and
  // measuring it at 24" reserved band the vanity never occupies.
  const depth = cornerBoxFor(ctx);
  const bands = (run.segments || []).map((s) =>
    s?.wall ? { wall: s.wall, depth, span: segmentSpan(s) } : null
  );
  const keepOuts = openingKeepOuts(bands, zones, {
    standing: (ctx.neighbours || []).map((n) => n.footprint),
  })[segIndex];
  const res = [];
  for (const { start, end, zone } of keepOuts) {
    for (const [lo, hi] of subtractSpans(start, end, measured)) {
      res.push({ lo, hi, openingId: zone.openingId });
    }
  }
  res.sort((a, b) => a.lo - b.lo);
  return res;
}

/** The stretch of its wall a segment covers, or undefined when unstated. */
function segmentSpan(seg) {
  const { startParam: a, endParam: b } = seg;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return undefined;
  return { lo: Math.min(a, b), hi: Math.max(a, b) };
}

/**
 * The run's openings sorted into the zone builder's three lists by `_kind`.
 * One with no kind is a door: never a passage, so never judged on what is
 * left of it.
 */
function openingsByKind(openings) {
  const out = { doors: [], windows: [], openings: [] };
  for (const o of openings) {
    if (o._kind === 'window') out.windows.push(o);
    else if (o._kind === 'opening') out.openings.push(o);
    else out.doors.push(o);
  }
  return out;
}

/**
 * Build the per-opening clearance zones (door-swing / stand-back) for every
 * opening on the run's walls, reusing the generator's zone builder. Needs
 * `ctx.walls` (engine walls) + `ctx.openings` tagged with `_kind`.
 */
function openingClearanceZones(ctx) {
  if (!Array.isArray(ctx.walls)) return [];
  const ops = ctx.openings || [];
  return getOpeningClearanceZones({
    walls: ctx.walls,
    doors: ops.filter((o) => o._kind === 'door'),
    windows: ops.filter((o) => o._kind === 'window'),
    openings: ops.filter((o) => o._kind === 'opening'),
  });
}

/**
 * TALL run objects (a dropped fridge, etc.) whose footprint intrudes on the
 * clear space in front of an opening on an ADJACENT wall — base cabinets sit
 * below the door swing and are exempt (`objectConflictsWithZones` only checks
 * TALL_TYPES and skips same-wall openings). Returns
 * [{ object, openingId, openingKind }].
 */
function tallItemOpeningConflicts(objects, ctx) {
  const zones = openingClearanceZones(ctx);
  if (zones.length === 0) return [];
  const wallById = new Map(ctx.walls.map((w) => [w.id, w]));
  const conflicts = [];
  for (const obj of objects || []) {
    const zone = objectConflictsWithZones(obj, zones, wallById);
    if (zone) {
      conflicts.push({
        object: obj,
        openingId: zone.openingId,
        openingKind: zone.kind,
      });
    }
  }
  return conflicts;
}

/**
 * The bottom two rungs of the corner ladder, for a corner that cannot carry a
 * full L: `dA` / `dB` are how far each leg is clear from the vertex, `depth`
 * the box one leg's body sweeps along the other wall.
 *   - 'blind': both legs are clear for a depth, so ONE runs to the vertex —
 *              the leg with the longer clear run — and the other stops a
 *              depth short of it.
 *   - 'empty': something stands within a depth of the vertex; both legs stop.
 * Shared by BOTH tiers: the lower plan reads it at the run's cabinet depth,
 * the upper plan (`upperTier.planUpperCorners`) at an upper's 12".
 */
function blindOrEmptyCorner(dA, dB, depth) {
  if (legTakes(dA, depth) && legTakes(dB, depth))
    return dA >= dB
      ? { mode: 'blind', extend: 'A', trimA: 0, trimB: depth }
      : { mode: 'blind', extend: 'B', trimA: depth, trimB: 0 };
  return { mode: 'empty', trimA: depth, trimB: depth };
}

/**
 * How to treat the corner at the vertex between segA (its END) and segB (its
 * START), given the openings, plus the per-leg trim to apply:
 *   - 'cabinet': a full L-corner cabinet fits — both legs clear for an L the
 *                line sells (`lSet`: 36", or Venus's 33"). `length` is its leg.
 *   - 'blind':   a full corner cabinet would clip an opening, but one leg can
 *                still run a cabinet to the vertex (both legs clear for the 24"
 *                cabinet depth). `extend` is that leg ('A'|'B'); the OTHER leg
 *                stops a cabinet-depth short so the two legs don't collide.
 *   - 'empty':   an opening sits within a cabinet depth of the vertex — nothing
 *                can reach it.
 * `trimA` / `trimB` are the param amounts to trim off segA's END / segB's START.
 * `hard.anchored` says the hard pieces are measured objects that cannot be
 * slid; `hard.pinnedA` / `hard.pinnedB` say one leg's nearest piece cannot
 * (see the gate below).
 */
function cornerPlan(segA, segB, ctx, hard = {}, lSet = DEFAULT_CORNER_L_SET) {
  const m = measureCorner(segA, segB, ctx, hard, lSet);
  if (lFits(m, m.footprint))
    return {
      mode: 'cabinet',
      length: m.footprint,
      trimA: m.footprint,
      trimB: m.footprint,
    };
  return blindOrEmptyCorner(
    Math.min(m.openA - OPENING_BUFFER, m.hardA),
    Math.min(m.openB - OPENING_BUFFER, m.hardB),
    m.depth
  );
}

/**
 * Whether an L with legs of `leg` metres fits the measured corner: both legs
 * clear of openings AND of the appliances for the whole leg. The corner yields
 * to an appliance; it never moves one (see `measureCorner`). An opening also
 * keeps its 4" keep-out clear (`OPENING_BUFFER`), as the fill packs against
 * it and as the kitchen generator measures a corner leg (user, 2026-11-04:
 * "fix the gap so it is consistent" — an L 2" from a doorway stood in it on a
 * drawn run where the generator left the leg empty). `measureCorner` keeps the
 * true distance to the opening; the keep-out is in what a leg needs, so the
 * panel can still say where the opening starts.
 */
function lFits(m, leg) {
  return (
    legTakes(m.openA, leg + OPENING_BUFFER) &&
    legTakes(m.openB, leg + OPENING_BUFFER) &&
    legTakes(m.hardA, leg) &&
    legTakes(m.hardB, leg)
  );
}

/**
 * The L this corner gets, from the sizes the run's line sells (`lSet`, in
 * inches; `families.cornerLSet`): the widest that fits, else the SMALLEST —
 * the one a leg would have to clear, which is what an option graded
 * infeasible reports it needs. A line selling only the 36" answers exactly
 * the `CORNER_CABINET_FOOTPRINT` this used to be fixed at.
 */
function lFootprintFor(m, lSet) {
  const legs = (lSet?.sizes?.length ? lSet.sizes : DEFAULT_CORNER_L_SET.sizes)
    .map((inches) => inches * INCHES_TO_METERS)
    .sort((a, b) => b - a);
  return legs.find((leg) => lFits(m, leg)) ?? legs[legs.length - 1];
}

/**
 * What `cornerPlan` decides ON, measured once: how far each leg is clear of a
 * blocking OPENING and of a HARD piece, and the gate each has to clear. Split
 * out so the options a user is offered at this corner
 * (`lowerCornerTreatment.gradeLowerCorner`) are graded on the very numbers the
 * plan reads — an option is feasible exactly when the plan could have chosen
 * it.
 *
 * `footprint` is the L the line would build here (`lFootprintFor`), and the
 * gates are taken at it.
 *
 * @returns {{ footprint, depth, openA, openB, openIdA, openIdB, hardA, hardB,
 *   gateA, gateB }} metres; a distance is Infinity when nothing stands there
 */
function measureCorner(
  segA,
  segB,
  ctx,
  hard = {},
  lSet = DEFAULT_CORNER_L_SET
) {
  const counterTopY = counterTopYOf(ctx);
  const ops = ctx.openings;
  // The corner box on this run: what the perpendicular leg's body sweeps along
  // the other wall, which gates the blind corner and sets both trims. The
  // run's own depth, not the standard 24" — see `cornerBoxFor`.
  const DEPTH = cornerBoxFor(ctx);
  const dirA = Math.sign(segA.endParam - segA.startParam) || 1;
  const dirB = Math.sign(segB.endParam - segB.startParam) || 1;
  // Openings gate the FULL corner at the L's footprint (the L's doors need
  // the room). A HARD piece (an appliance the caller measured) only vetoes
  // when it INTRUDES into the footprint — a range parked 24" from the vertex
  // beside a corner cabinet is a normal kitchen, and the stricter gate made
  // a run re-solve delete the generator's own corner unit (user-seen
  // 2026-08-31).
  const openA = nearestBlockingOpeningAt(
    segA.wall,
    segA.endParam,
    -dirA,
    ops,
    counterTopY
  );
  const openB = nearestBlockingOpeningAt(
    segB.wall,
    segB.startParam,
    dirB,
    ops,
    counterTopY
  );
  // How much room a HARD piece must leave for the L corner cabinet: the
  // whole leg. THE CORNER YIELDS TO AN APPLIANCE — a range, fridge, dishwasher,
  // cooktop or wall oven, scanned or generated — and falls down the menu
  // (L -> blind -> empty) instead of moving it: the one yield rule
  // (`claude-docs/2026-09-10-kitchen-corner-units.md` §3.6, adopted in the
  // engine 2026-10-19). A SINK is the exception, and it is not measured here
  // on the generated path (`nearestHardPieceToEnd` skips it): the corner beats
  // a sink base, which the packer slides clear — unless it cannot slide, when
  // it arrives as `pinnedSinkToEnd`'s distance; on the scan path it gives the
  // corner at most `MAX_SCAN_APPLIANCE_SLIDE` (`sinkSlackToEnd`).
  //
  // The generated path used to gate an appliance at the 24" box alone and let
  // the packer slide it out of the L (2026-08-31, when a stricter gate made a
  // re-solve delete the generator's own corner unit). That reason is gone —
  // the generator's corner is RECORDED on the run and honoured unchecked
  // (`cornerTreatment.js`, 2026-09-12) — and what was left was the engine
  // moving appliances wherever it decided a corner itself: a drawn run, or a
  // user's "Corner cabinet" pick pushed a dishwasher 34.6" -> 40.3" from the
  // vertex to fit a 36" L (Golden West, 2026-10-19). Over the corpus's
  // generated runs, with their corner records removed, it had moved 766
  // appliances. (FB5B0707 is the scan-path version of the same thing: a
  // cooktop 26.1" from the vertex cleared a 24" gate and the bay slid 9.9" out
  // from under a cooktop that stayed put.)
  const m = {
    depth: DEPTH,
    openA: openA.d,
    openB: openB.d,
    openIdA: openA.id,
    openIdB: openB.id,
    hardA: hard.A ?? Infinity,
    hardB: hard.B ?? Infinity,
  };
  const FOOT = lFootprintFor(m, lSet);
  return { ...m, footprint: FOOT, gateA: FOOT, gateB: FOOT };
}

/**
 * A square corner with a leg that holds NO cabinet: an opening on that leg
 * starts past the corner box, but too close to it for a real cabinet between
 * the box and the opening's keep-out — the kitchen generator's leg rule
 * (`kitchen-assembly/legRule.legHolds`, 12"), asked of the run's own box
 * (`cornerBoxFor`) and the keep-out the fill would pack against
 * (`OPENING_BUFFER`). Such a corner is not a corner: the other leg runs
 * straight to the wall and nothing stands on this one before its opening (the
 * user, 2026-11-03: "the cab run should just be straight and butt into the
 * adjoining wall. i.e. no corner cabs just normal ones"). The generator never
 * builds one — its leg rule refuses the corner — so only a drawn run, which
 * the person drew across the doorway's wall, ever meets it.
 *
 * Openings only: an appliance on a leg is something the corner turns into.
 * Null when both legs hold a cabinet, when neither does, or when the opening
 * stands inside the box itself (the plan's empty corner).
 *
 * @returns {{ leg: 'A'|'B', trim: number, openingId: string|null }|null}
 *   `trim` — the dead leg's corner trim, from the vertex to its opening
 */
function deadCornerLeg(segA, segB, ctx) {
  const counterTopY = counterTopYOf(ctx);
  const box = cornerBoxFor(ctx);
  const dirA = Math.sign(segA.endParam - segA.startParam) || 1;
  const dirB = Math.sign(segB.endParam - segB.startParam) || 1;
  const a = nearestBlockingOpeningAt(
    segA.wall,
    segA.endParam,
    -dirA,
    ctx.openings,
    counterTopY
  );
  const b = nearestBlockingOpeningAt(
    segB.wall,
    segB.startParam,
    dirB,
    ctx.openings,
    counterTopY
  );
  const holds = (d) =>
    !Number.isFinite(d) || legHolds(d - OPENING_BUFFER - box);
  const holdsA = holds(a.d);
  const holdsB = holds(b.d);
  if (holdsA === holdsB) return null;
  const dead = holdsA ? { leg: 'B', near: b } : { leg: 'A', near: a };
  if (!legTakes(dead.near.d, box)) return null;
  return { leg: dead.leg, trim: dead.near.d, openingId: dead.near.id };
}

/**
 * Floor-standing appliance bays that COVER a same-wall opening they rise
 * above — a 50" fridge swapped in beside a window now stands across half of
 * it (user-found 2026-09-05 on Golden West / Scan 1).
 *
 * The packer cannot prevent this: an appliance bay is `hard`, tiled around
 * where it stands, so WIDENING one never re-tests the wall's openings. The
 * generator's `applianceBlocksWindow` runs at PLACEMENT time only and is
 * never reached from here. So report it rather than move the user's
 * appliance — the same treatment `tallItemOpeningConflicts` gives an
 * adjacent-wall opening, one wall over.
 *
 * Hosted kinds are exempt: a sink or cooktop sits at counter height and
 * tucks under a window like the base cabinet it is cut into.
 *
 * @param {Object} run rehydrated run (segments carry `.wall`)
 * @param {Object} ctx `{ floorY, openings }`
 * @param {Object[]} solvedBays `fillRun`'s solved bay table
 * @returns {{bayId, kind, openingId, overlap}[]}
 */
function applianceOpeningOverlaps(run, ctx, solvedBays) {
  const out = [];
  const floorY = ctx.floorY || 0;
  const bayById = new Map(baysForRun(run).map((b) => [b.id, b]));
  for (const sb of solvedBays || []) {
    const bay = bayById.get(sb.bayId);
    if (!bay || !isApplianceKind(bay.kind)) continue;
    if (HOSTED_BAY_KINDS.has(bay.kind)) continue;
    if (!(bay.height > 0)) continue;
    const wall = (run.segments || [])[sb.segIndex]?.wall;
    if (!wall) continue;
    const topY = floorY + bay.height;
    for (const o of ctx.openings || []) {
      if (!o?.center || !o.dimensions) continue;
      if ((o.parentWallIds || [])[0] !== wall.id) continue;
      const sillY = o.center.y - (o.dimensions.y || 0) / 2;
      if (topY <= sillY + SILL_EPS) continue; // tucks under it
      const p = worldToWallParam(wall, o.center);
      if (p == null) continue;
      const half = (o.dimensions.x || 0) / 2;
      const overlap = Math.min(sb.end, p + half) - Math.max(sb.start, p - half);
      if (overlap > 1e-4)
        out.push({
          bayId: bay.id,
          kind: RUN_TYPE_BY_BAY_KIND[bay.kind] || bay.kind,
          openingId: o.id,
          overlap,
        });
    }
  }
  return out;
}

module.exports = {
  counterTopYOf,
  openingReservationsForSegment,
  applianceOpeningOverlaps,
  allOpeningReservationsForSegment,
  adjacentOpeningReservationsForSegment,
  tallItemOpeningConflicts,
  blocksBaseCabinet,
  cornerPlan,
  deadCornerLeg,
  measureCorner,
  lFits,
  blindOrEmptyCorner,
  COUNTERTOP_HEIGHT,
};
